import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { Menu, X, Globe, Euro, Gauge } from 'lucide-react';
import { CustomSelect } from './CustomSelect';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const Header = () => {
  const { language, setLanguage, currency, setCurrency, t, isAuthenticated, logout } = useApp();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [enableUtilityIndex, setEnableUtilityIndex] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/settings/global`);
        setEnableUtilityIndex(data.enable_utility_index !== false);
      } catch (e) {
        // Default to enabled if fetch fails
        setEnableUtilityIndex(true);
      }
    };
    fetchSettings();
  }, []);

  const indexLabel = language === 'en' ? 'Meter Reading' : language === 'fr' ? 'Relevé Compteur' : 'Transmitere Index';

  const navLinks = [
    { path: '/', label: t('home') },
    { path: '/apartments', label: t('apartments') },
    { path: '/about', label: t('about') },
    { path: '/contact', label: t('contact') },
    ...(enableUtilityIndex ? [{ path: '/utility-index', label: indexLabel, icon: Gauge }] : []),
  ];

  return (
    <header
      data-testid="main-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-slate-950/90 backdrop-blur-sm py-4 border-b border-white/10' : 'bg-transparent py-6'
      }`}
    >
      <div className="container mx-auto px-6">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" data-testid="logo-link" className="text-2xl font-serif italic text-primary">
            RENTIASI.RO
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.label.toLowerCase()}`}
                className={`text-sm uppercase tracking-wider transition-colors ${
                  location.pathname === link.path
                    ? 'text-primary'
                    : 'text-foreground hover:text-primary'
                }`}
              >
                {link.label}
              </Link>
            ))}
            {isAuthenticated && (
              <Link
                to="/admin"
                data-testid="nav-link-admin"
                className="text-sm uppercase tracking-wider text-foreground hover:text-primary transition-colors"
              >
                {t('admin')}
              </Link>
            )}
          </nav>

          {/* Language & Currency Selectors */}
          <div className="hidden md:flex items-center space-x-4">
            {/* Language Selector */}
            <CustomSelect
              value={language.toUpperCase()}
              onChange={(val) => setLanguage(val.toLowerCase())}
              options={[
                { value: 'RO', label: 'RO' },
                { value: 'EN', label: 'EN' },
                { value: 'FR', label: 'FR' },
              ]}
              icon={Globe}
              testId="language-selector"
            />

            {/* Currency Selector */}
            <CustomSelect
              value={currency}
              onChange={setCurrency}
              options={[
                { value: 'EUR', label: 'EUR' },
                { value: 'RON', label: 'RON' },
              ]}
              icon={Euro}
              testId="currency-selector"
            />

            {isAuthenticated && (
              <button
                data-testid="logout-button"
                onClick={logout}
                className="text-sm uppercase tracking-wider text-foreground hover:text-primary transition-colors"
              >
                {t('logout')}
              </button>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-button"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden text-foreground"
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div data-testid="mobile-menu" className="md:hidden mt-6 pb-4">
            <div className="flex flex-col space-y-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsMenuOpen(false)}
                  className="text-sm uppercase tracking-wider text-foreground hover:text-primary transition-colors"
                >
                  {link.label}
                </Link>
              ))}
              {isAuthenticated && (
                <Link
                  to="/admin"
                  onClick={() => setIsMenuOpen(false)}
                  className="text-sm uppercase tracking-wider text-foreground hover:text-primary transition-colors"
                >
                  {t('admin')}
                </Link>
              )}
              <div className="flex items-center space-x-4 pt-4 border-t border-white/10">
                <select
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  className="bg-slate-900 border border-white/20 text-foreground px-3 py-1 text-sm rounded-full"
                >
                  <option value="ro" className="bg-slate-900 text-foreground">RO</option>
                  <option value="en" className="bg-slate-900 text-foreground">EN</option>
                  <option value="fr" className="bg-slate-900 text-foreground">FR</option>
                </select>
                <select
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                  className="bg-slate-900 border border-white/20 text-foreground px-3 py-1 text-sm rounded-full"
                >
                  <option value="EUR" className="bg-slate-900 text-foreground">EUR</option>
                  <option value="RON" className="bg-slate-900 text-foreground">RON</option>
                </select>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};